/**
 * @file     ListingActions.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  The listing action set read from the CTA config: one primary,
 *           the secondaries, the state's note, then Add to list, Compare,
 *           Share and the More menu. `ListingActionButton` is the single
 *           button the top bar and phone bar reuse, so every surface draws an
 *           action the same way, on the slug page and in the quick views.
 */
'use client';

import { useId, useState, type MouseEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { ACTION_LABEL, type ActionId, type CtaPosition } from '@/core/config/listingActions';
import { listingDetailContent as L } from '@/core/content/listingDetailSharedContent';
import { hubColors } from '@/components/hub/hubKitStyles';
import { glassMaterial } from '@/design-system/tokens/glass';
import { layoutTokens } from '@/design-system/tokens/layout';
import { motionTokens } from '@/design-system/tokens/motion';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { zLayers } from '@/design-system/tokens';
import { useBackToClose } from '@/core/overlays/useBackToClose';
import { useListingActions } from './ListingActionsContext';
import { ListingGateCard } from '../ListingGate';

/** Lucide-style 24-grid strokes, drawn at 18px. */
const ICON_PATHS: Record<ActionId | 'list' | 'compare' | 'share' | 'more' | 'chevron', ReactNode> = {
  book_showing: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  attend_open_house: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  ask_question: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  get_listing_alerts: <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />,
  get_area_alerts: <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />,
  see_similar: <path d="M5 12h14M12 5l7 7-7 7" />,
  value_my_home: <path d="M3 17l6-6 4 4 8-8M14 7h7v7" />,
  request_documents: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 13h8M8 17h8" />
    </>
  ),
  save_pdf: <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />,
  make_offer: <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />,
  broker_intro: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  create_account: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  sign_in: <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />,
  request_viewing: <path d="M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM4 10h16M8 3v4M16 3v4M9 15l2 2 4-4" />,
  rental_alerts: <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />,
  list: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  compare: <path d="M16 3l4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" />,
  share: <path d="M12 3v12M7 8l5-5 5 5M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </>
  ),
  chevron: <path d="M9 18l6-6-6-6" />,
};

export function ActionIcon({ name, size = 18, filled = false }: { name: keyof typeof ICON_PATHS; size?: number; filled?: boolean }) {
  return (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{ flexShrink: 0 }}
    >
      {ICON_PATHS[name]}
    </Box>
  );
}

const focusRing = {
  '&.Mui-focusVisible, &:focus-visible': {
    outline: `2px solid ${hubColors.interactiveFocusRing}`,
    outlineOffset: 2,
  },
} as const;

const motion = `${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`;

const buttonSx = (variant: 'primary' | 'secondary', height: number) => ({
  minHeight: height,
  px: 4,
  gap: 2,
  borderRadius: `${layoutTokens.radius.md}px`,
  fontFamily: 'inherit',
  fontSize: height >= 48 ? 15 : 14,
  fontWeight: fontWeightTokens.medium,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  transition: `background-color ${motion}, border-color ${motion}, color ${motion}`,
  ...(variant === 'primary'
    ? {
        backgroundColor: hubColors.interactivePrimary,
        color: hubColors.textOnPrimary,
        '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.primaryHover } },
      }
    : {
        border: `1px solid ${hubColors.borderStrong}`,
        backgroundColor: 'transparent',
        color: hubColors.textPrimary,
        '@media (hover: hover)': { '&:hover': { borderColor: hubColors.textSecondary, backgroundColor: hubColors.interactiveHover } },
      }),
  ...focusRing,
});

interface ButtonProps {
  id: ActionId;
  variant: 'primary' | 'secondary';
  position: CtaPosition;
  /** 48 in the rail and the phone bar, 40 in the top bar. */
  height?: 48 | 40;
  fullWidth?: boolean;
  /** Show the open-house time under Attend open house (rail only). */
  detail?: boolean;
  tabIndex?: number;
}

export function ListingActionButton({ id, variant, position, height = 48, fullWidth = false, detail = false, tabIndex }: ButtonProps) {
  const { t } = useTranslation();
  const { target, listing, showingRequested, offerAttempted, viewingRequested } = useListingActions();
  const action = target(id, position);
  const label = t(ACTION_LABEL[id].key, { defaultValue: ACTION_LABEL[id].defaultValue });
  const oh = listing.openHouse;
  const when =
    detail && id === 'attend_open_house' && oh
      ? oh.start && oh.end
        ? t(L.summary.openHouseWhen.key, { defaultValue: L.summary.openHouseWhen.defaultValue, day: oh.day, start: oh.start, end: oh.end })
        : oh.day
      : null;
  const done =
    (id === 'book_showing' && showingRequested) || (id === 'make_offer' && offerAttempted) || (id === 'request_viewing' && viewingRequested)
      ? t('listingActions.done.requested', { defaultValue: 'Requested' })
      : null;

  return (
    <ButtonBase
      component={action.href ? 'a' : 'button'}
      href={action.href}
      onClick={(event: MouseEvent) => action.onClick(event)}
      tabIndex={tabIndex}
      data-action={id}
      sx={{
        ...buttonSx(variant, height),
        width: fullWidth ? '100%' : 'auto',
        justifyContent: when ? 'flex-start' : 'center',
        textAlign: when ? 'left' : 'center',
        py: when ? 2 : 0,
      }}
    >
      <ActionIcon name={id} />
      {when ? (
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          <Box component="span" sx={{ display: 'block' }}>{label}</Box>
          <Box component="span" sx={{ display: 'block', fontSize: 13, fontWeight: fontWeightTokens.regular, opacity: 0.9 }}>{when}</Box>
        </Box>
      ) : (
        <span>{done ? `${label} · ${done}` : label}</span>
      )}
      {when ? <ActionIcon name="chevron" /> : null}
    </ButtonBase>
  );
}

const iconCircleSx = (pressed: boolean) => ({
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: `${layoutTokens.radius.pill}px`,
  border: `1px solid ${pressed ? hubColors.interactivePrimary : hubColors.borderStrong}`,
  color: pressed ? hubColors.interactivePrimary : hubColors.textPrimary,
  backgroundColor: pressed ? hubColors.chip : 'transparent',
  transition: `background-color ${motion}, border-color ${motion}, color ${motion}`,
  '@media (hover: hover)': { '&:hover': { borderColor: hubColors.textSecondary, backgroundColor: hubColors.interactiveHover } },
  '&.Mui-disabled': { opacity: 0.5 },
  ...focusRing,
});

/** Add to list, Compare, Share and More, in the rail's focus order. */
function ActionIconRow() {
  const { t } = useTranslation();
  const { actions, saved, savePending, toggleSave, inCompare, compareFull, toggleCompare, share, target } = useListingActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  useBackToClose(anchor != null, () => setAnchor(null));
  const tr = (e: { key: string; defaultValue: string }) => t(e.key, { defaultValue: e.defaultValue });
  const compareLabel = inCompare ? tr(L.actions.inCompare) : compareFull ? tr(L.actions.compareFull) : tr(L.actions.compare);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <ButtonBase
        onClick={toggleSave}
        disabled={savePending}
        aria-pressed={saved}
        sx={{ ...buttonSx('secondary', 44), flex: 1, minWidth: 0, ...(saved ? { borderColor: hubColors.interactivePrimary, color: hubColors.interactivePrimary } : null) }}
      >
        <ActionIcon name="list" filled={saved} />
        <span>{saved ? tr(L.actions.onYourList) : tr(L.actions.addToList)}</span>
      </ButtonBase>
      <ButtonBase
        onClick={toggleCompare}
        disabled={compareFull}
        aria-pressed={inCompare}
        aria-label={compareLabel}
        title={compareLabel}
        sx={iconCircleSx(inCompare)}
      >
        <ActionIcon name="compare" />
      </ButtonBase>
      <ButtonBase
        onClick={share}
        aria-label={tr(L.actions.share)}
        title={tr(L.actions.share)}
        // Under 360 Share moves into More.
        sx={{ ...iconCircleSx(false), '@media (max-width: 359.95px)': { display: 'none' } }}
      >
        <ActionIcon name="share" />
      </ButtonBase>
      {actions.menu.length > 0 ? (
        <>
          <ButtonBase
            onClick={(event) => setAnchor(event.currentTarget)}
            aria-label={tr(L.actions.more)}
            aria-haspopup="menu"
            aria-expanded={anchor != null}
            aria-controls={anchor ? menuId : undefined}
            sx={{ ...iconCircleSx(anchor != null) }}
          >
            <ActionIcon name="more" />
          </ButtonBase>
          <Menu
            id={menuId}
            anchorEl={anchor}
            open={anchor != null}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: zLayers.modal }}
            slotProps={{
              paper: {
                sx: {
                  mt: 2,
                  minWidth: 240,
                  p: 1,
                  borderRadius: `${layoutTokens.radius.lg}px`,
                  boxShadow: 'none',
                  ...glassMaterial('regular', { edge: 'all' }),
                },
              },
              list: { sx: { p: 0 } },
            }}
          >
            {actions.menu.map((id) => {
              const item = target(id, 'menu');
              return (
                <MenuItem
                  key={id}
                  component={item.href ? 'a' : 'li'}
                  href={item.href}
                  onClick={(event: MouseEvent) => {
                    setAnchor(null);
                    item.onClick(event);
                  }}
                  sx={{ minHeight: 44, gap: 3, borderRadius: `${layoutTokens.radius.sm}px`, fontSize: 14, color: hubColors.textPrimary }}
                >
                  <ActionIcon name={id} />
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{t(ACTION_LABEL[id].key, { defaultValue: ACTION_LABEL[id].defaultValue })}</span>
                    {id === 'broker_intro' ? (
                      <Box component="span" sx={{ fontSize: 12, color: hubColors.textMuted }}>{tr(L.actions.brokerHint)}</Box>
                    ) : null}
                  </Box>
                </MenuItem>
              );
            })}
            <MenuItem
              onClick={() => {
                setAnchor(null);
                share();
              }}
              sx={{ minHeight: 44, gap: 3, borderRadius: `${layoutTokens.radius.sm}px`, fontSize: 14, '@media (min-width: 360px)': { display: 'none' } }}
            >
              <ActionIcon name="share" />
              <span>{tr(L.actions.share)}</span>
            </MenuItem>
          </Menu>
        </>
      ) : null}
    </Box>
  );
}

interface Props {
  /** The element the top bar and phone bar watch: they show once it leaves the viewport. */
  anchorId?: string;
  position?: CtaPosition;
  /** Buttons a host draws elsewhere (a quick view's phone footer), left out here. */
  omit?: ActionId[];
}

/** Primary, secondaries, the note and the icon row. One primary on screen at a time. */
export function ListingActions({ anchorId, position = 'rail', omit = [] }: Props) {
  const { t } = useTranslation();
  const { actions, listing, gated } = useListingActions();
  const secondary = actions.secondary.filter((id) => !omit.includes(id));
  const note = actions.note
    ? t(actions.note.key, {
        defaultValue: actions.note.defaultValue,
        place: listing.neighbourhood ?? listing.city ?? 'BC',
        type: listing.propertyNoun,
      })
    : null;

  return (
    <Box
      id={anchorId}
      role="group"
      aria-label={t(L.actions.groupLabel.key, { defaultValue: L.actions.groupLabel.defaultValue })}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', minWidth: 0 }}
    >
      {/* Past the free preview the preview card takes the buttons' place. The
          anchor element stays, so the bars' observer keeps watching it. */}
      {gated ? <ListingGateCard bare /> : null}
      {gated || omit.includes(actions.primary) ? null : (
        <ListingActionButton id={actions.primary} variant="primary" position={position} fullWidth detail />
      )}
      {!gated && secondary.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: secondary.length > 1 ? '1fr 1fr' : '1fr', gap: 2 }}>
          {secondary.map((id) => (
            <ListingActionButton key={id} id={id} variant="secondary" position={position} fullWidth />
          ))}
        </Box>
      ) : null}
      {note && !gated ? (
        <Box component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.45, color: hubColors.textSecondary }}>
          {note}
        </Box>
      ) : null}
      {actions.icons ? <ActionIconRow /> : null}
    </Box>
  );
}
